/**
 * `lt.delivery.v1` — THE PER-TURN DELIVERY ACTIVITY SIGNAL, browser side.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHAT THIS ANSWERS, AND WHY NOTHING ELSE COULD
 *
 * The owner's question is "where is my audio right now?" — specifically, what is
 * happening during the pause between finishing a sentence and the other person
 * reacting. Answering it needs a fact no browser can observe for itself:
 *
 *   1. A `recipient_mix` track's SUBSCRIPTION is granted to its own recipient's
 *      LiveKit identity only (worker `delivery/recipient_mix_coordinator.py`), so
 *      a speaker's browser never receives — and cannot measure the energy of —
 *      the translated audio being played to the person they are talking to.
 *   2. The one thing broadcast to everyone, the route envelope attribute
 *      `lt.route.v1.<sid>`, carries a SESSION lifecycle state, not a per-turn
 *      one: `SessionState.STREAMING` persists for the whole active session
 *      (worker `session_state.py`) and never toggles per utterance.
 *
 * So the worker states it explicitly, addressed to the SOURCE participant: one
 * `active` when translation of their audio toward a recipient starts flowing,
 * one `drained` when the last of it has been RELEASED from that recipient's
 * paced mix. `drained` is a PLAYOUT fact, not a generation fact — which is the
 * whole point: "the text is written" is not "they have heard it".
 *
 * ════════════════════════════════════════════════════════════════════════════
 * `(epoch, seq)` IS THE ORDER. `at_ms` IS NOT (design review, 2026-08-31)
 *
 * `at_ms` is a producer wall clock and cannot separate DUPLICATES from
 * REORDERING: the same event arriving twice over two transports carries one
 * timestamp, and two events minted inside the same millisecond are
 * indistinguishable. `epoch` is the source speaker's turn lineage and `seq` is
 * monotonic within it, so the pair totally orders one recipient's stream and
 * names a repeat exactly.
 *
 * The failure this closes is specific and would be visible to the owner: an OLD
 * `drained` overtaking a NEWER `active` clears a delivery that is still running,
 * so the light says "they have heard you" in the middle of your sentence still
 * being played. `at_ms` alone admitted it. See {@link compareDeliveryLineage} —
 * one comparison, used by the reducer and its tests alike.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * `active` MAY ARRIVE EARLY, AND THAT IS THE POINT
 *
 * The worker arms `active` from the speaker's ORIGINAL speech activity, before
 * any translated audio exists. So a recipient is usually already outstanding by
 * the moment the viewer stops talking, and the light moves speaking -> delivering
 * with no gap to paper over. Consumers must not assume `active` implies audio is
 * already playing; it means "this is on its way".
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHAT THIS FILE IS NOT
 *
 * It is not a timer, and it must never grow one. The surface it replaced
 * (`useOtherPartyHearingTranslationTail`) estimated the tail as a fixed 3.5s
 * window after local speech stopped, which is wrong in both directions — it
 * declares delivery finished while a long sentence is still playing, and claims
 * delivery is in flight on a call where nothing was ever translated. A status
 * light that guesses is worse than none, because the owner will believe it.
 */

/** The one contract version this build accepts. */
export const DELIVERY_ACTIVITY_CONTRACT_VERSION = 'live-translation.delivery-activity.v1';

/** The LiveKit text-stream topic the worker publishes on. */
export const DELIVERY_ACTIVITY_TOPIC = 'lt.delivery.v1';

/**
 * `active` — translation of this source's audio toward this recipient is on its
 * way. `drained` — the last of it has been released from that recipient's paced
 * mix. See {@link DeliveryReleaseReason} for what a `drained` actually witnessed.
 */
export type DeliveryActivityState = 'active' | 'drained';

/**
 * WHY a delivery ended, and the difference matters to what the light may CLAIM.
 *
 *   `drained`    — normal playout completion. The audio finished on the other
 *                  end. This is the only reason that earns "your translation was
 *                  delivered" out loud.
 *   `stale_cap`  — the worker's own fail-open timeout: it stopped waiting. The
 *                  audio may well have played, but nobody observed it, so the
 *                  indicator leaves `delivering` (a light that sticks is the
 *                  worse failure) and lands on the `delivered` look WITHOUT
 *                  asserting delivery. It is recorded as its own basis so its
 *                  rate is measurable — a `stale_cap` that stops being rare is
 *                  a defect in the worker's drain, not something to widen here.
 */
export type DeliveryReleaseReason = 'drained' | 'stale_cap';

interface DeliveryActivityBase {
  contract_version: typeof DELIVERY_ACTIVITY_CONTRACT_VERSION;
  /**
   * Always `'source'`. The event is addressed to the participant whose SPEECH
   * is being delivered, never to the listener — a listener already hears the
   * audio and needs no light to tell them so.
   */
  role: 'source';
  /** The speaker's controller-minted `stable_participant_id`. */
  source_participant_id: string;
  /** The listener this activity concerns, also a `stable_participant_id`. */
  recipient_id: string;
  /** The source speaker's turn lineage. Higher always supersedes. */
  epoch: number;
  /** Monotonic within `epoch`. With it, `(epoch, seq)` totally orders. */
  seq: number;
  /** Producer wall clock, milliseconds. Telemetry — NOT the ordering key. */
  at_ms: number;
}

export interface DeliveryActivityActiveV1 extends DeliveryActivityBase {
  state: 'active';
  /**
   * Optional here on purpose — see `validateDeliveryActivityV1`. Nothing reads
   * it on an `active`; a delivery that has not ended has not been released.
   */
  release_reason?: DeliveryReleaseReason;
}

export interface DeliveryActivityDrainedV1 extends DeliveryActivityBase {
  state: 'drained';
  /** REQUIRED on a drain, so a consumer can never silently assume the good one. */
  release_reason: DeliveryReleaseReason;
}

export type DeliveryActivityV1 = DeliveryActivityActiveV1 | DeliveryActivityDrainedV1;

/** The eight keys every event must carry. */
const REQUIRED_KEYS = [
  'at_ms',
  'contract_version',
  'epoch',
  'recipient_id',
  'role',
  'seq',
  'source_participant_id',
  'state',
] as const;

/**
 * `release_reason` is the one key that may be absent.
 *
 * ⚠️ A DELIBERATE ASYMMETRY, and the reason is which way each mistake fails.
 * The reviewed shape lists `release_reason` unconditionally, but the change note
 * describes it as a property OF a `drained`. If the worker omits it on `active`
 * and this build required it, EVERY `active` would be refused and state 2 would
 * never appear — a total outage of the feature, from strictness. If the worker
 * always sends it and this build merely tolerates it on `active`, nothing is
 * lost: it is validated when present and read by nobody.
 *
 * So: required on `drained`, optional on `active`, and never unvalidated. This
 * is the conservative reading; if the producer settles on always-present it can
 * be tightened with no consumer change.
 */
const OPTIONAL_KEYS = ['release_reason'] as const;

const REQUIRED_KEY_SET: ReadonlySet<string> = new Set(REQUIRED_KEYS);
const ALLOWED_KEY_SET: ReadonlySet<string> = new Set<string>([
  ...REQUIRED_KEYS,
  ...OPTIONAL_KEYS,
]);

/** `translation_participants.stable_participant_id` is `text`, bounded. */
const MAX_PARTICIPANT_ID_LENGTH = 256;

const DELIVERY_STATES: ReadonlySet<string> = new Set<DeliveryActivityState>(['active', 'drained']);
const RELEASE_REASONS: ReadonlySet<string> = new Set<DeliveryReleaseReason>([
  'drained',
  'stale_cap',
]);

export type DeliveryActivityValidation =
  | { valid: true; event: DeliveryActivityV1 }
  | { valid: false; reason: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) !== null
  );
}

/** Untrimmed-equal, non-blank, bounded — the same rule the append RPCs apply. */
function isCanonicalParticipantId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 1 &&
    value.length <= MAX_PARTICIPANT_ID_LENGTH &&
    value.trim() === value
  );
}

/**
 * A counter this build can order on: a non-negative safe integer.
 *
 * ZERO IS REAL, for both `epoch` and `seq` — a first turn and its first event.
 * Treating 0 as "unset" is a mistake this repository has already made once with
 * translation generations, and it admits a stranger's row against a default.
 */
function isCounter(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/** One recipient's position in the source speaker's stream. */
export interface DeliveryLineagePosition {
  epoch: number;
  seq: number;
}

/**
 * Total order over one recipient's events: negative if `a` precedes `b`, zero if
 * they are the SAME event seen twice, positive if `a` supersedes `b`.
 *
 * Epoch dominates. A new turn's `seq` restarts, so comparing `seq` first would
 * make every new turn look like a regression of the previous one and refuse it.
 */
export function compareDeliveryLineage(
  a: DeliveryLineagePosition,
  b: DeliveryLineagePosition,
): number {
  if (a.epoch !== b.epoch) return a.epoch < b.epoch ? -1 : 1;
  if (a.seq !== b.seq) return a.seq < b.seq ? -1 : 1;
  return 0;
}

/**
 * Validate one delivery-activity event.
 *
 * The reason is CLOSED vocabulary, never built from the input: it is counted and
 * logged, and the payload arrives over a data channel any participant can write
 * to. Nothing here interprets a malformed event charitably — an event this build
 * cannot read means the producer and this consumer disagree, and guessing which
 * half is right is how a status light starts lying.
 */
export function validateDeliveryActivityV1(value: unknown): DeliveryActivityValidation {
  if (!isPlainObject(value)) return { valid: false, reason: 'not_an_object' };

  // A CLOSED key set: every required key present, and nothing outside the
  // allowed set. An event that has grown a key is one this build has not been
  // taught to read, and admitting it while ignoring the extra is how a producer
  // and a consumer drift apart in silence.
  const keys = Object.keys(value);
  for (const key of keys) {
    if (!ALLOWED_KEY_SET.has(key)) return { valid: false, reason: 'unexpected_key_set' };
  }
  for (const key of REQUIRED_KEY_SET) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      return { valid: false, reason: 'unexpected_key_set' };
    }
  }

  if (value.contract_version !== DELIVERY_ACTIVITY_CONTRACT_VERSION) {
    return { valid: false, reason: 'contract_version_mismatch' };
  }
  if (value.role !== 'source') return { valid: false, reason: 'role_not_source' };
  if (!isCanonicalParticipantId(value.source_participant_id)) {
    return { valid: false, reason: 'source_participant_id_not_canonical' };
  }
  if (!isCanonicalParticipantId(value.recipient_id)) {
    return { valid: false, reason: 'recipient_id_not_canonical' };
  }
  if (typeof value.state !== 'string' || !DELIVERY_STATES.has(value.state)) {
    return { valid: false, reason: 'state_not_recognised' };
  }
  // The ordering pair. A non-integer or negative counter cannot order anything,
  // and a NaN would make every comparison in `compareDeliveryLineage` false —
  // which is precisely how a stale `drained` would slip past the fence.
  if (!isCounter(value.epoch)) return { valid: false, reason: 'epoch_not_a_counter' };
  if (!isCounter(value.seq)) return { valid: false, reason: 'seq_not_a_counter' };
  if (!isCounter(value.at_ms)) return { valid: false, reason: 'at_ms_not_a_clock' };

  const hasReleaseReason = Object.prototype.hasOwnProperty.call(value, 'release_reason');
  if (hasReleaseReason) {
    if (typeof value.release_reason !== 'string' || !RELEASE_REASONS.has(value.release_reason)) {
      return { valid: false, reason: 'release_reason_not_recognised' };
    }
  } else if (value.state === 'drained') {
    // Never defaulted. A drain whose reason is missing would otherwise be read
    // as the good one, and the indicator would announce a delivery nobody saw.
    return { valid: false, reason: 'release_reason_missing_on_drained' };
  }

  return { valid: true, event: value as unknown as DeliveryActivityV1 };
}
